import {
    languageIdValPath,
    integerRegex,
    invalidSignInErrorMessage,
    signInErrorTransKey,
    posGenericErrorMessage,
    posNoTransactionMessage
} from '../constants/Constants';
import { LoginType } from '../constants/LoginTypeEnum';
import { SignUpType } from '../constants/SignUpTypeEnum';
import {
    FieldValueType,
    IFieldsValidations,
    IRampBUConfig,
    IRequestFieldsAdditionalMappings,
    PrimitiveValueType
} from '../internal/domain/entities/Config';
import { IFacebookLogin, ILogin } from '../internal/domain/entities/Login';
import { ConfigRepository } from '../internal/repositories/ConfigRepository';
import { handlePlaceholderValues } from '../internal/utils/FormatText';
import { diContainer, serviceNames } from '../service-setup/diContainer';
import {
    SignUpResponse,
    IUserApiResponse,
    FeatureProcessResultType,
    IUserSignInApiResponse
} from './RampPgApiSdk';

export interface IUserRequestData {
    [key: string]: PrimitiveValueType;
}

const invalidCredentialErrorCodes = ['invalid.password.signin', 'user.signin.account_not_found'];

export type ISignInRequestData = { emailAddress: string; password: string } | { token: string };

export type SignInError = typeof invalidSignInErrorMessage | typeof signInErrorTransKey;

// TODO: APACFTRS1-770 consolidate form values interface definition
export interface IStoreData {
    [key: string]: PrimitiveValueType | undefined;
}

const mapPosFrontiersResult = ({ resultCode }: FeatureProcessResultType) => {
    if (resultCode === -4) {
        return posNoTransactionMessage;
    } else if ([0, 1, 2].includes(resultCode)) {
        return undefined;
    } else {
        return posGenericErrorMessage;
    }
};

const convertFieldType = (expectedType?: FieldValueType, originData: PrimitiveValueType = '') => {
    const originType = typeof originData as FieldValueType;
    switch (expectedType) {
        case 'number':
            if (originType === 'boolean') {
                // Uses 1/0 for boolean values
                return originData ? 1 : 0;
            } else if (originType === 'string') {
                const strData = originData.toString();
                if (integerRegex.test(strData)) {
                    // Integer stored as string
                    return parseInt(strData, 10);
                }

                return strData.toLowerCase() === 'true' ? 1 : 0;
            }

            return originData;

        case 'boolean':
            if (originType === 'string') {
                if (['false', '0'].includes(originData.toString().toLowerCase())) {
                    return false;
                }
            }

            return Boolean(originData);

        case 'string':
            return originData.toString();

        default:
            return originData;
    }
};

const getAdditionalMappingValue = (
    storeData: IStoreData,
    additionalMapping: PrimitiveValueType,
    expectedType?: FieldValueType
) => {
    if (['number', 'boolean'].includes(typeof additionalMapping)) {
        // Bypass converting fixed values
        return additionalMapping;
    } else {
        const parsedValue = handlePlaceholderValues(additionalMapping as string, storeData);

        return convertFieldType(expectedType, parsedValue);
    }
};

const getRequestData = (
    storeData: IStoreData,
    requestFields: string[],
    fieldsValidations: IFieldsValidations,
    fieldAdditionalMappings: IRequestFieldsAdditionalMappings
): IUserRequestData => {
    return requestFields.reduce((payload, field) => {
        const expectedType = fieldsValidations[field]?.type;
        const formValue = storeData[field];
        if (fieldAdditionalMappings[field]) {
            return {
                ...payload,
                [field]: getAdditionalMappingValue(
                    storeData,
                    fieldAdditionalMappings[field],
                    // Fixed values does not have type specification
                    expectedType
                )
            };
        } else if (formValue === undefined) {
            // Skip untouched field that is not mandatory
            return payload;
        } else {
            return {
                ...payload,
                // There will be type specifications for all form fields
                [field]: convertFieldType(expectedType, storeData[field])
            };
        }
    }, {} as IUserRequestData);
};

const handleInverseFields = (data: IStoreData, inverseFields: string[]) => {
    inverseFields.forEach((field) => {
        if (typeof data[field] === 'boolean') {
            data[field] = !data[field];
        }
    });
};

// TODO: APACFTRS1-770: is it good to access a repository from DataMapper
const formSignUpRequest = (data: IStoreData, signUpType: SignUpType) => {
    const { rampConfig, inverseFields, config } = diContainer.get(
        serviceNames.configRepository
    ) as ConfigRepository;
    const {
        postRequestFields,
        putRequestFields,
        fieldsValidations,
        requestFieldsAdditionalMappings
    } = rampConfig as IRampBUConfig;

    // TODO: APACFTRS1-770 create class to handle these operations?
    data.languageId = config.config[languageIdValPath];
    handleInverseFields(data, inverseFields);

    return getRequestData(
        data,
        signUpType === SignUpType.RAMP ? postRequestFields : putRequestFields,
        fieldsValidations,
        requestFieldsAdditionalMappings
    );
};

const mapUserApiResponse = (data: IUserApiResponse): SignUpResponse | Record<string, never> => {
    const { users, features } = data;
    if (users?.length > 0) {
        // There might be more than one matched users in the future.
        const { userId, userStatusKey } = users[0];
        const addUserResponse: SignUpResponse = {
            userId,
            userStatusKey
        };
        if (features?.posFrontiers) {
            addUserResponse.posFrontiersErrorKey = mapPosFrontiersResult(features?.posFrontiers);
        }

        return addUserResponse;
    }

    return {};
};

const DataMappings = {
    toAddUser: {
        request(data: IStoreData): IUserRequestData {
            return formSignUpRequest(data, SignUpType.RAMP);
        }
    },
    fromAddUser: {
        response: mapUserApiResponse
    },
    toUpdateUser: {
        request(data: IStoreData): IUserRequestData {
            return formSignUpRequest(data, SignUpType.ONLINE);
        }
    },
    fromUpdateUser: {
        response: mapUserApiResponse
    },
    patchPassword: {
        response: mapUserApiResponse
    },
    toSignIn: {
        request(data: {
            formData: ILogin | IFacebookLogin;
            loginType?: LoginType;
        }): ISignInRequestData {
            const { formData, loginType } = data;
            if (loginType === LoginType.FACEBOOK) {
                return { token: (formData as IFacebookLogin).accessToken };
            } else {
                const { email, password } = formData as ILogin;

                return { emailAddress: email, password };
            }
        }
    },
    fromSignInError: {
        response(data: IUserSignInApiResponse): SignInError {
            if (data?.errors?.length) {
                const isCredentialError = data.errors.some((error) =>
                    invalidCredentialErrorCodes.includes(error.key)
                );

                return isCredentialError ? invalidSignInErrorMessage : signInErrorTransKey;
            }

            return signInErrorTransKey;
        }
    }
};

export default DataMappings;
